/**
 * ============================================
 * PROCESSADOR DE ARQUIVOS - CHAT GERAL
 * ============================================
 * 
 * Versão exclusiva do Chat Geral usando RPC para resolver RLS 42501.
 * Cópia de fileMessageProcessor.ts com salvamento via RPC exclusiva.
 * 
 * NÃO USAR EM:
 * - Document Chat
 * - AI-inline
 */

import { v4 as uuidv4 } from 'uuid';
import { processFile } from '@/utils/fileProcessing';
import { Message, FileObject } from '@/types';
import { saveGeneralMessageRPC } from './services/generalChatMessageService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { analyzeDocument, DocumentAnalysis } from './documentAnalysisService';

/**
 * Converts file to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Processa arquivo e cria mensagem de usuário - EXCLUSIVO CHAT GERAL
 * Usa RPC exclusiva para salvar mensagens
 */
export async function processGeneralFileAndCreateMessage(
  file: File | null,
  content: string,
  conversationId: string,
  userId: string,
  onProgress?: (progress: number) => void
): Promise<{
  userMessage: Message;
  fileObject: FileObject | null;
  extractedText: string;
  documentAnalysis?: DocumentAnalysis;
}> {
  const messageId = uuidv4();
  const messageTimestamp = new Date().toISOString();
  let fileObject: FileObject | null = null;
  let extractedText = '';
  let localFileUrl = '';
  let documentAnalysis: DocumentAnalysis | undefined;
  
  // Criar mensagem do usuário
  const messageContent = file ? (content || `Uploaded file: ${file.name}`) : content;
  
  // 🎯 SALVAR MENSAGEM VIA RPC EXCLUSIVA DO CHAT GERAL
  console.log('💾 [CHAT GERAL] Salvando mensagem do usuário via RPC...');
  const savedMessage = await saveGeneralMessageRPC({
    conversationId,
    role: 'user',
    content: messageContent,
    fileId: null
  });
  
  const userMessage: Message = {
    id: savedMessage.id,
    conversation_id: conversationId,
    role: "user",
    content: messageContent,
    file: null,
    created_at: savedMessage.created_at,
  };
  
  // Processar arquivo se presente
  if (file) {
    try {
      console.log(`🔄 [CHAT GERAL] Processando arquivo: ${file.name} (${file.type})`);
      const fileId = uuidv4();
      
      const processResult = await processFile(file, onProgress);
      extractedText = processResult.text;
      localFileUrl = processResult.localUrl || '';
      
      const isImageFile = file.type.startsWith('image/');
      
      // Imagens: usar Google Vision API
      if (isImageFile) {
        console.log("🖼️ [CHAT GERAL] Imagem detectada - usando Google Vision API");
        
        try {
          console.log("🔄 Convertendo imagem para base64...");
          const base64Data = await fileToBase64(file);
          console.log("✅ Imagem convertida, tamanho:", base64Data.length);
          
          const { data: sessionData } = await supabase.auth.getSession();
          
          console.log("📡 Chamando Google Vision API...");
          const visionResponse = await supabase.functions.invoke('google-vision', {
            body: {
              imageBase64: base64Data,
              language: 'pt-BR',
              maxExtraction: true,
              enhancedMode: true
            },
            headers: {
              Authorization: `Bearer ${sessionData.session?.access_token}`
            }
          });
          
          console.log("🔍 Resposta Google Vision:", {
            success: !visionResponse.error,
            error: visionResponse.error?.message || null,
            textLength: visionResponse.data?.text?.length || 0
          });
          
          if (visionResponse.error) {
            console.error("❌ Erro Google Vision:", visionResponse.error);
            toast({
              title: "Erro no Google Vision",
              description: `Falha na API: ${visionResponse.error.message}`,
              variant: "destructive",
            });
            throw new Error(`Google Vision API error: ${visionResponse.error.message}`);
          }
          
          if (visionResponse.data?.text && visionResponse.data.text.length > 0) {
            const visionText = visionResponse.data.text.trim();
            console.log(`✅ Google Vision extraiu: ${visionText.length} caracteres`);
            extractedText = visionText;
            
            toast({
              title: "Google Vision - Sucesso",
              description: `Texto extraído: ${visionText.length} caracteres`,
              variant: "default",
            });
            
            if (extractedText.length > 50) {
              console.log("📋 Analisando documento com texto do Google Vision...");
              documentAnalysis = await analyzeDocument(
                extractedText, 
                visionResponse.data.structured_data
              );
              console.log("📊 Análise concluída:", documentAnalysis?.documentType || 'unknown');
            }
          } else {
            console.error("❌ Google Vision não retornou texto");
            toast({
              title: "Google Vision - Sem Texto",
              description: "Não foi possível detectar texto na imagem.",
              variant: "destructive",
            });
            throw new Error("Google Vision returned no text");
          }
        } catch (visionError) {
          console.error("❌ Falha no processamento Google Vision:", visionError);
          
          let errorMessage = "Falha ao processar imagem com Google Vision API.";
          if (visionError.message.includes("key not configured")) {
            errorMessage = "Chave API do Google Vision não configurada.";
          } else if (visionError.message.includes("returned no text")) {
            errorMessage = "Nenhum texto detectado na imagem.";
          }
          
          toast({
            title: "Erro na Extração de Imagem",
            description: errorMessage,
            variant: "destructive",
          });
          throw visionError;
        }
      } else {
        // Documentos não-imagem
        console.log("📄 [CHAT GERAL] Processamento de documento não-imagem");
        
        if (extractedText.length > 100) {
          console.log("📄 Analisando documento não-imagem...");
          documentAnalysis = await analyzeDocument(extractedText);
          console.log("📊 Análise concluída:", documentAnalysis?.documentType || 'unknown');
        }
      }
      
      console.log(`📊 [CHAT GERAL] Resumo final:`, {
        fileType: file.type,
        isImage: isImageFile,
        textLength: extractedText.length,
        hasAnalysis: !!documentAnalysis
      });
      
      // Criar registro de arquivo no banco
      const fileDbObject = {
        id: fileId,
        conversation_id: conversationId,
        message_id: savedMessage.id,
        name: file.name,
        type: file.type,
        size: file.size,
        url: null,
        processed: true,
        extracted_text: extractedText,
        created_at: messageTimestamp
      };
      
      const { error: fileError } = await supabase
        .from('files')
        .insert(fileDbObject);
        
      if (fileError) {
        console.error("❌ [CHAT GERAL] Erro ao salvar registro de arquivo:", fileError);
        
        if (fileError.message.includes("violates row-level security policy")) {
          toast({
            title: "Erro de Permissão",
            description: "Você não tem permissão para salvar registros de arquivos.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erro de Banco de Dados",
            description: "Não foi possível salvar as informações do arquivo.",
            variant: "default",
          });
        }
      } else {
        console.log("💾 [CHAT GERAL] Registro de arquivo salvo com sucesso");
        
        // Atualizar mensagem com file_id
        const { error: updateMessageError } = await supabase
          .from('messages')
          .update({ file_id: fileId })
          .eq('id', savedMessage.id);
          
        if (updateMessageError) {
          console.error("❌ Erro ao atualizar mensagem com file ID:", updateMessageError);
        }
        
        // Criar objeto de arquivo para estado local
        fileObject = {
          id: fileId,
          conversation_id: conversationId,
          message_id: savedMessage.id,
          name: file.name,
          type: file.type,
          size: file.size,
          url: localFileUrl,
          processed: true,
          extracted_text: extractedText,
          created_at: messageTimestamp,
        };
        
        userMessage.file = fileObject;
      }
      
    } catch (fileError) {
      console.error("❌ [CHAT GERAL] Erro ao processar arquivo:", fileError);
      
      if (file.type.startsWith('image/')) {
        throw fileError;
      } else {
        toast({
          title: "Erro de Arquivo",
          description: "Falha ao processar o arquivo. Continuando sem ele.",
          variant: "destructive",
        });
      }
    }
  }
  
  console.log("✅ [CHAT GERAL] Processamento de arquivo concluído:", {
    hasFile: !!file,
    extractedTextLength: extractedText.length,
    hasDocumentAnalysis: !!documentAnalysis,
    documentType: documentAnalysis?.documentType || 'none'
  });
  
  return { userMessage, fileObject, extractedText, documentAnalysis };
}
